import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';

const MacroRegimesEditor = () => {
  const [regimes, setRegimes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingRegime, setEditingRegime] = useState(null);
  const [formData, setFormData] = useState({ regime_name: '', start_date: '', end_date: '', description: '' });

  useEffect(() => {
    fetchRegimes();
  }, []);

  const fetchRegimes = async () => {
    setIsLoading(true);
    try {
      const data = await adminAPI.getMacroRegimes();
      setRegimes(data);
    } catch (err) {
      console.error('Error fetching macro regimes:', err);
      setMessage({ type: 'error', text: 'Failed to load macro regimes' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      const result = await adminAPI.seedMacroRegimes();
      setMessage({ type: 'success', text: result.message });
      fetchRegimes();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to seed regimes' });
    }
  };

  const handleEdit = (regime) => {
    setEditingRegime(regime.id);
    setFormData({
      regime_name: regime.regime_name,
      start_date: regime.start_date,
      end_date: regime.end_date,
      description: regime.description || '',
    });
  };

  const handleAdd = () => {
    setEditingRegime('new');
    setFormData({ regime_name: '', start_date: '', end_date: '', description: '' });
  };

  const handleCancel = () => {
    setEditingRegime(null);
    setFormData({ regime_name: '', start_date: '', end_date: '', description: '' });
  };

  const handleSave = async () => {
    if (!formData.regime_name || !formData.start_date || !formData.end_date) {
      setMessage({ type: 'error', text: 'Name, start date and end date are required' });
      return;
    }
    try {
      if (editingRegime === 'new') {
        await adminAPI.createMacroRegime(formData);
        setMessage({ type: 'success', text: 'Regime created' });
      } else {
        await adminAPI.updateMacroRegime(editingRegime, formData);
        setMessage({ type: 'success', text: 'Regime updated' });
      }
      setEditingRegime(null);
      setFormData({ regime_name: '', start_date: '', end_date: '', description: '' });
      fetchRegimes();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save regime' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this macro regime?')) return;
    try {
      await adminAPI.deleteMacroRegime(id);
      setMessage({ type: 'success', text: 'Regime deleted' });
      fetchRegimes();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete regime' });
    }
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Macro Regimes</h5>
        <div>
          {regimes.length === 0 && (
            <button className="btn btn-outline-secondary btn-sm me-2" onClick={handleSeed}>
              Seed Defaults
            </button>
          )}
          <button
            className="btn btn-sm"
            style={{ backgroundColor: 'var(--secondary-color)', color: '#fff' }}
            onClick={handleAdd}
          >
            + Add Regime
          </button>
        </div>
      </div>
      <div className="card-body">
        <p className="text-muted small mb-3">
          Define historical regime windows (e.g. "Sticky Inflation 2021–24"). The CMA covariance compute pipeline blends per-regime correlation matrices using per-scenario weights, so a scenario can express views like "60% Normal + 40% High Inflation". Regimes may overlap in time.
        </p>

        {message && (
          <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible fade show`}>
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm" role="status"></div>
            <span className="ms-2">Loading...</span>
          </div>
        ) : (
          <>
            {editingRegime !== null && (
              <div className="card mb-3" style={{ borderColor: 'var(--input-border)' }}>
                <div className="card-body">
                  <h6>{editingRegime === 'new' ? 'Add Macro Regime' : 'Edit Macro Regime'}</h6>
                  <div className="row g-2 mb-2">
                    <div className="col-md-4">
                      <label className="form-label small">Regime Name</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={formData.regime_name}
                        onChange={(e) => setFormData({ ...formData, regime_name: e.target.value })}
                        placeholder="e.g. High Inflation (1970s)"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small">Start Date</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small">End Date</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label small">Description</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm"
                      style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}
                      onClick={handleSave}
                    >
                      Save
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={handleCancel}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Regime Name</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Description</th>
                    <th style={{ width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {regimes.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-3">
                        No macro regimes configured. Click "Seed Defaults" to add standard regimes.
                      </td>
                    </tr>
                  ) : (
                    regimes.map((regime) => (
                      <tr key={regime.id}>
                        <td>{regime.regime_name}</td>
                        <td>{regime.start_date}</td>
                        <td>{regime.end_date}</td>
                        <td>{regime.description}</td>
                        <td>
                          <button
                            className="btn btn-outline-primary btn-sm me-1"
                            onClick={() => handleEdit(regime)}
                            title="Edit"
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(regime.id)}
                            title="Delete"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MacroRegimesEditor;
